import { useCallback, useLayoutEffect, useRef } from 'react';
import { attachFit, detachFit, requestFit } from '../utils/fitRegistry';
import { MIN_FONT_PX } from '../utils/fitScale';

/**
 * Returns a callback ref for a `.fit-scroll` wrapper. The wrapper is
 * registered with the page-wide fit scheduler on mount and released on
 * unmount; after every commit (rows loaded, a column added, Net/Gross toggled)
 * a re-measure is requested in a microtask — i.e. before paint, so no flash.
 */
export function useFitToWidth<T extends HTMLElement = HTMLDivElement>(
  opts: { minFontPx?: number } = {},
): (node: T | null) => void {
  const { minFontPx = MIN_FONT_PX } = opts;
  const elRef = useRef<T | null>(null);

  const ref = useCallback((node: T | null) => {
    if (elRef.current && elRef.current !== node) detachFit(elRef.current);
    elRef.current = node;
    if (node) attachFit(node, { minFontPx });
  }, [minFontPx]);

  // No deps on purpose: any render of the owner may have changed the content.
  useLayoutEffect(() => {
    if (elRef.current) requestFit(elRef.current, 'microtask');
  });

  return ref;
}
